import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    Card,
    Checkbox,
    DatePicker,
    Form,
    Input,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    Upload,
    message,
} from "antd";
import { PlusOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ApplicationManagementApi from "../api/ApplicationManagementApi";
import useDocumentTitle from "../hooks/useDocumentTitle";
import {faEdit, faRemove} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import useAuth from "../hooks/useAuth";
import {resetMenu} from "../redux/slices/menuSlice";
import {useDispatch} from "react-redux";
import {Link} from "react-router-dom";

const { RangePicker } = DatePicker;

const sortableColumns = {
    date: "date",
    number: "number",
    companyName: "company",
};

const statusColorMap = {
    new: "blue",
    in_progress: "orange",
    done: "green",
};

const mapUploadValue = (e) => e?.fileList || [];

const ApplicationsManagementPage = () => {
    useDocumentTitle("Документы по обучению");

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(resetMenu());
    }, [dispatch]);

    const { user, setUser, roles, isAdminOrManager, isAdminOrManagerOrPartner, isPartner }  = useAuth();

    const [form] = Form.useForm();

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [filters, setFilters] = useState({
        companyId: undefined,
        status: undefined,
        dateRange: null,
        hasOriginal: false,
        sortBy: "date_desc",
    });

    const [companies, setCompanies] = useState([]);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [docsModalOpen, setDocsModalOpen] = useState(false);
    const [docsModalTitle, setDocsModalTitle] = useState("");
    const [docsModalItems, setDocsModalItems] = useState([]);

    const [mode, setMode] = useState("create");
    const [active, setActive] = useState(null);

    const fetchCompanies = useCallback(async (query = "") => {
        try {
            const data = await ApplicationManagementApi.listCompanies(query);
            setCompanies(data || []);
        } catch (e) {
            message.error(e?.message || "Не удалось загрузить компании");
        }
    }, []);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page,
                pageSize,
                companyId: filters.companyId,
                status: filters.status,
                hasOriginal: filters.hasOriginal,
                sortBy: filters.sortBy,
                dateFrom: filters.dateRange?.[0]?.format("YYYY-MM-DD"),
                dateTo: filters.dateRange?.[1]?.format("YYYY-MM-DD"),
            };
            const data = await ApplicationManagementApi.list(params);
            setItems(data?.items || []);
            setTotal(Number(data?.total || 0));
        } catch (e) {
            message.error(e?.message || "Не удалось загрузить заявки");
        } finally {
            setLoading(false);
        }
    }, [filters, page, pageSize]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    useEffect(() => {
        fetchCompanies("");
    }, [fetchCompanies]);

    const openCreate = () => {
        if (!isAdminOrManagerOrPartner) return;
        setMode("create");
        setActive(null);
        form.resetFields();
        form.setFieldsValue({ status: "new", date: dayjs() });
        setEditModalOpen(true);
    };

    const openEdit = (record) => {
        setMode("edit");
        setActive(record);
        form.setFieldsValue({
            date: record.date ? dayjs(record.date) : null,
            number: record.number,
            companyId: record.companyId,
            kmocDocuments: (record.kmocDocuments || []).map((file) => ({ uid: file.id, name: file.name, status: "done", url: file.url, response: file })),
            curatorDocuments: (record.curatorDocuments || []).map((file) => ({ uid: file.id, name: file.name, status: "done", url: file.url, response: file })),
            originalSent: record.originalSent,
            status: record.status,
        });
        setEditModalOpen(true);
    };

    const openEditCuratorDocs = (record) => {
        setMode("edit_curator_docs");
        setActive(record);
        form.setFieldsValue({
            date: record.date ? dayjs(record.date) : null,
            number: record.number,
            companyId: record.companyId,
            kmocDocuments: (record.kmocDocuments || []).map((file) => ({ uid: file.id, name: file.name, status: "done", url: file.url, response: file })),
            curatorDocuments: (record.curatorDocuments || []).map((file) => ({ uid: file.id, name: file.name, status: "done", url: file.url, response: file })),
            originalSent: record.originalSent,
            status: record.status,
        });
        setEditModalOpen(true);
    };

    const openDocsModal = (title, files) => {
        setDocsModalTitle(title);
        setDocsModalItems(files || []);
        setDocsModalOpen(true);
    };

    const extractFiles = (fileList = []) =>
        fileList
            .filter((f) => f.status === "done")
            .map((f) => f.response || { id: f.uid, name: f.name, url: f.url || "#" });

    const onSubmit = async () => {
        try {
            const values = await form.validateFields();
            const company = companies.find((x) => x.id === values.companyId);
            const payload = mode === "edit_curator_docs"
                ? {
                    ...active,
                    curatorDocuments: extractFiles(values.curatorDocuments),
                    updatedAt: dayjs().format("YYYY-MM-DD HH:mm"),
                }
                : {
                    date: values.date.format("YYYY-MM-DD"),
                    number: values.number.trim(),
                    companyId: values.companyId,
                    companyName: company?.name,
                    kmocDocuments: extractFiles(values.kmocDocuments),
                    curatorDocuments: extractFiles(values.curatorDocuments),
                    originalSent: values.originalSent?.trim() || "",
                    status: values.status,
                    updatedAt: dayjs().format("YYYY-MM-DD HH:mm"),
                };

            if (mode === "create") {
                await ApplicationManagementApi.create(payload);
                message.success("Заявка создана");
                setPage(1);
            } else if (mode === "edit") {
                await ApplicationManagementApi.update(active.id, payload);
                message.success("Заявка обновлена");
            } else {
                await ApplicationManagementApi.update(active.id, payload);
                message.success("Документы куратора обновлены");
            }

            setEditModalOpen(false);
            await fetchList();
        } catch (e) {
            if (e?.errorFields) return;
            message.error(e?.message || "Не удалось сохранить заявку");
        }
    };

    const onDelete = async (id) => {
        try {
            await ApplicationManagementApi.remove(id);
            message.success("Заявка удалена");
            if (items.length === 1 && page > 1) {
                setPage((prev) => prev - 1);
            } else {
                fetchList();
            }
        } catch (e) {
            message.error(e?.message || "Не удалось удалить заявку");
        }
    };

    const buildUploadProps = () => ({
        multiple: true,
        customRequest: async ({ file, onSuccess, onError, onProgress }) => {
            try {
                const uploaded = await ApplicationManagementApi.uploadFile(file, onProgress);
                onSuccess(uploaded);
            } catch (e) {
                onError(e);
            }
        },
    });

    const renderFilesCell = (title, files = []) => {
        if (!files.length) return <Typography.Text type="secondary">Нет файлов</Typography.Text>;
        if (files.length === 1) {
            const file = files[0];
            return (
                <a href={file.url || "#"} target="_blank" rel="noreferrer" download={file.name}>
                    {file.name}
                </a>
            );
        }
        return <Button size="small" onClick={() => openDocsModal(title, files)}>Посмотреть файлы ({files.length})</Button>;
    };

    const statusLabelMap = useMemo(
        () => ApplicationManagementApi.statuses.reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {}),
        []
    );

    const getSortOrder = (field) => {
        if (!filters.sortBy.startsWith(`${field}_`)) return null;
        return filters.sortBy.endsWith("asc") ? "ascend" : "descend";
    };

    const handleTableChange = (_, __, sorter) => {
        const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        if (!nextSorter?.field || !sortableColumns[nextSorter.field]) return;

        if (!nextSorter.order) {
            setPage(1);
            setFilters((prev) => ({ ...prev, sortBy: "date_desc" }));
            return;
        }

        const nextDirection = nextSorter.order === "ascend" ? "asc" : "desc";
        setPage(1);
        setFilters((prev) => ({
            ...prev,
            sortBy: `${sortableColumns[nextSorter.field]}_${nextDirection}`,
        }));
    };

    const columns = [
        {
            title: "Дата",
            dataIndex: "date",
            key: "date",
            width: 170,
            sorter: true,
            sortOrder: getSortOrder("date"),
            responsive: ['md'],
        },
        {
            title: "№ заявки",
            dataIndex: "number",
            key: "number",
            width: 140,
            sorter: true,
            sortOrder: getSortOrder("number"),
            render: (value) => <Typography.Text strong>{value}</Typography.Text>,
            responsive: ['md'],
        },
        {
            title: "Компания",
            dataIndex: "companyName",
            key: "companyName",
            width: 240,
            sorter: true,
            sortOrder: getSortOrder("company"),
            responsive: ['md'],
        },
        {
            title: "Документы КМОЦ",
            key: "kmocDocuments",
            render: (_, record) => renderFilesCell("Документы КМОЦ", record.kmocDocuments),
            responsive: ['md'],
        },
        {
            title: "Документы компании",
            key: "curatorDocuments",
            render: (_, record) => renderFilesCell("Документы компании", record.curatorDocuments),
            responsive: ['md'],
        },
        {
            title: "Оригинал отправлен",
            dataIndex: "originalSent",
            key: "originalSent",
            render: (value) => value || <Typography.Text type="secondary">—</Typography.Text>,
            responsive: ['md'],
        },
        {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            width: 140,
            render: (value) => <Tag color={statusColorMap[value] || "default"}>{statusLabelMap[value] || value}</Tag>,
            responsive: ['md'],
        },
        {
            title: "Дата обновления",
            dataIndex: "updatedAt",
            key: "updatedAt",
            width: 170,
            responsive: ['md'],
        },
    ];

    if (isAdminOrManagerOrPartner) {
        columns.push({
            title: "Создана",
            key: "createdBy",
            responsive: ['md'],
            render: (_, record) =>
                isPartner ?
                    <Typography.Text>{record.createdBy.name}</Typography.Text>
                    :
                <Link to={'/cabinet/users/' + record.createdBy.id}>{record.createdBy.name}</Link>
        })
    }

    columns.push({
        title: "Действия",
        key: "actions",
        responsive: ['md'],
        render: (_, record) => (
            <Space>
                {
                    roles.includes('ROLE_CURATOR') && record.status !== 'done' &&
                    <button onClick={() => openEditCuratorDocs(record)} className="btn h-8 w-8 p-0 text-info hover:bg-info/20 focus:bg-info/20 active:bg-info/25">
                        <FontAwesomeIcon icon={faEdit}/>
                    </button>
                }
                {
                    (
                        roles.includes('ROLE_ADMIN') ||
                        ((roles.includes('ROLE_MANAGER') || roles.includes('ROLE_PARTNER')) && record.status !== 'done')
                    ) &&
                    <button onClick={() => openEdit(record)} className="btn h-8 w-8 p-0 text-info hover:bg-info/20 focus:bg-info/20 active:bg-info/25">
                        <FontAwesomeIcon icon={faEdit}/>
                    </button>
                }
                {
                    (
                        roles.includes('ROLE_ADMIN') ||
                        ((roles.includes('ROLE_MANAGER') || roles.includes('ROLE_PARTNER')) && record.status !== 'done')
                    ) &&
                    <Popconfirm
                        title="Удалить заявку?"
                        description="Действие нельзя отменить"
                        okText="Удалить"
                        okButtonProps={{ danger: true }}
                        cancelText="Отмена"
                        onConfirm={() => onDelete(record.id)}
                    >
                        <button className="btn h-8 w-8 p-0 text-error hover:bg-error/20 focus:bg-error/20 active:bg-error/25">
                            <FontAwesomeIcon icon={faRemove}/>
                        </button>
                    </Popconfirm>
                }

            </Space>
        ),
    });

    columns.push({
        title: 'Информация о заявке',
        responsive: ['xs'],
        render: (_, record) => {
            return <>
                <div>Заявка №{record.number} от {record.date}</div>
                <div><span className={'font-bold'}>Дата обновления:</span> {record.updatedAt}</div>
                <div>
                    <div className={'font-bold'}>Документы КМОЦ:</div>
                    {renderFilesCell("Документы КМОЦ", record.kmocDocuments)}
                </div>
                <div>
                    <div className={'font-bold'}>Документы компании:</div>
                    {renderFilesCell("Документы КМОЦ", record.curatorDocuments)}
                </div>
                <div><span className={'font-bold'}>Оригинал отправлен:</span>
                <span>{record.originalSent || <Typography.Text type="secondary">&nbsp;—</Typography.Text>}</span>
                </div>
                <div><span className={'font-bold'}>Статус:</span> <Tag color={statusColorMap[record.status] || "default"}>{statusLabelMap[record.status] || record.status}</Tag></div>
                {
                    isAdminOrManagerOrPartner &&
                    <div><span className={'font-bold'}>Создана:</span> {
                        isPartner ?
                            <Typography.Text>{record.createdBy.name}</Typography.Text>
                            :
                            <Link to={'/cabinet/users/' + record.createdBy.id}>{record.createdBy.name}</Link>
                    }</div>
                }

                <div class={'flex justify-center items-center space-x-2 mt-2'}>
                    {
                        roles.includes('ROLE_CURATOR') && record.status !== 'done' &&
                        <button onClick={() => openEditCuratorDocs(record)} className="btn h-8 w-8 p-0 text-info hover:bg-info/20 focus:bg-info/20 active:bg-info/25">
                            <FontAwesomeIcon icon={faEdit}/>
                        </button>
                    }
                    {
                        (
                            roles.includes('ROLE_ADMIN') ||
                            ((roles.includes('ROLE_MANAGER') || roles.includes('ROLE_PARTNER')) && record.status !== 'done')
                        ) &&
                        <button onClick={() => openEdit(record)} className="btn h-8 w-8 p-0 text-info hover:bg-info/20 focus:bg-info/20 active:bg-info/25">
                            <FontAwesomeIcon icon={faEdit}/>
                        </button>
                    }
                    {
                        (roles.includes('ROLE_ADMIN') || roles.includes('ROLE_MANAGER') || roles.includes('ROLE_PARTNER')) &&
                        <Popconfirm
                            title="Удалить заявку?"
                            description="Действие нельзя отменить"
                            okText="Удалить"
                            okButtonProps={{ danger: true }}
                            cancelText="Отмена"
                            onConfirm={() => onDelete(record.id)}
                        >
                            <button className="btn h-8 w-8 p-0 text-error hover:bg-error/20 focus:bg-error/20 active:bg-error/25">
                                <FontAwesomeIcon icon={faRemove}/>
                            </button>
                        </Popconfirm>
                    }

                </div>

            </>
        }
    });

    return (
        <div className="p-4">
            <Card
                title={
                    <div className="flex items-center justify-between gap-3 mob-flex-wrap mob-pb-4 mob-pt-2">
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            Документы по обучению
                        </Typography.Title>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={fetchList} />
                            { isAdminOrManagerOrPartner &&
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                                Создать заявку
                            </Button>
                            }
                        </Space>
                    </div>
                }
            >
                <Space wrap style={{ marginBottom: 16 }}>
                    <Select
                        allowClear
                        showSearch
                        filterOption={false}
                        placeholder="Фильтр по компании"
                        style={{ width: 260 }}
                        value={filters.companyId}
                        onSearch={(value) => fetchCompanies(value)}
                        onChange={(value) => {
                            setPage(1);
                            setFilters((prev) => ({ ...prev, companyId: value }));
                        }}
                        options={companies.map((item) => ({ value: item.id, label: item.name + ' [' + item.inn + ']' }))}
                    />

                    <RangePicker
                        value={filters.dateRange}
                        onChange={(value) => {
                            setPage(1);
                            setFilters((prev) => ({ ...prev, dateRange: value }));
                        }}
                    />

                    <Select
                        allowClear
                        placeholder="Фильтр по статусу"
                        style={{ width: 220 }}
                        value={filters.status}
                        onChange={(value) => {
                            setPage(1);
                            setFilters((prev) => ({ ...prev, status: value }));
                        }}
                        options={ApplicationManagementApi.statuses}
                    />

                    <Checkbox
                        checked={filters.hasOriginal}
                        onChange={(event) => {
                            setPage(1);
                            setFilters((prev) => ({ ...prev, hasOriginal: event.target.checked }));
                        }}
                    >
                        Оригинал отправлен
                    </Checkbox>

                    <Button
                        onClick={() => {
                            setPage(1);
                            setFilters({
                                companyId: undefined,
                                status: undefined,
                                dateRange: null,
                                hasOriginal: false,
                                sortBy: "date_desc",
                            });
                        }}
                    >
                        Сбросить фильтры
                    </Button>
                </Space>

                <Table
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={items}
                    /*scroll={{ x: 1300 }}*/
                    onChange={handleTableChange}
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        onChange: (nextPage, nextPageSize) => {
                            setPage(nextPage);
                            setPageSize(nextPageSize);
                        },
                    }}
                />
            </Card>

            <Modal
                open={editModalOpen}
                title={mode === "create" ? "Создать заявку" : mode === "edit" ? "Редактировать заявку" : "Редактировать документы куратора"}
                onCancel={() => setEditModalOpen(false)}
                onOk={onSubmit}
                okText={mode === "create" ? "Создать" : "Сохранить"}
                cancelText="Отмена"
                width={760}
                maskClosable={false}
                destroyOnClose
            >
                <Form form={form} layout="vertical" preserve={false}>

                    {mode === "edit_curator_docs" ? (
                        <div class={'flex gap-4'}>
                            <b>Статус</b>
                            <span>
                                    <Tag color={statusColorMap[active?.status] || "default"}>{statusLabelMap[active?.status] || active?.status || "—"}</Tag>
                                </span>
                        </div>
                    ) : (
                        <Form.Item label="Статус" name="status" rules={[{ required: true, message: "Выберите статус" }]}>
                            <Select options={ApplicationManagementApi.statuses} placeholder="Выберите статус" />
                        </Form.Item>
                    )}

                    {mode === "edit_curator_docs" ? (
                        <div class={'flex gap-4'}>
                            <b>Дата</b>
                            <span>{active?.date || "—"}</span>
                        </div>
                    ) : (
                        <Form.Item label="Дата" name="date" rules={[{ required: true, message: "Выберите дату" }]}>
                            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
                        </Form.Item>
                    )}

                    {mode === "edit_curator_docs" ? (
                            <div class={'flex gap-4'}>
                                <b>№ заявки</b>
                                <span>{active?.number || "—"}</span>
                            </div>
                    ) : (
                        <Form.Item
                            label="№ заявки"
                            name="number"
                            rules={[{ required: true, message: "Введите номер заявки" }, { max: 64, message: "Слишком длинное значение" }]}
                        >
                            <Input placeholder="Например: REQ-105" />
                        </Form.Item>
                    )}

                    {mode === "edit_curator_docs" ? (
                            <div class={'flex gap-4'}>
                                <b>Компания</b>
                                <span>{active?.companyName || "—"}</span>
                            </div>
                    ) : (
                        <Form.Item label="Компания" name="companyId" rules={[{ required: true, message: "Выберите компанию" }]}>
                            <Select
                                showSearch
                                filterOption={false}
                                onSearch={(value) => fetchCompanies(value)}
                                options={companies.map((item) => ({ value: item.id, label: item.name + ' [' + item.inn + ']' }))}
                                placeholder="Выберите компанию"
                            />
                        </Form.Item>
                    )}

                    {mode === "edit_curator_docs" ? (
                            <div class={'flex gap-4'}>
                                <b>Документы КМОЦ</b>
                                <span>{renderFilesCell("Документы КМОЦ", active?.kmocDocuments || [])}</span>
                            </div>
                    ) : (
                        <Form.Item
                            label="Документы КМОЦ"
                            name="kmocDocuments"
                            valuePropName="fileList"
                            getValueFromEvent={mapUploadValue}
                        >
                            <Upload {...buildUploadProps()}>
                                <Button icon={<UploadOutlined />}>Выбрать и загрузить</Button>
                            </Upload>
                        </Form.Item>
                    )}

                    {mode === "edit_curator_docs" ? (
                            <div class={'flex gap-4'}>
                                <b>Оригинал отправлен</b>
                                <span>{active?.originalSent || "—"}</span>
                            </div>
                    ) : (
                        <Form.Item label="Оригинал отправлен" name="originalSent">
                            <Input placeholder="Текстовое поле (опционально)" />
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Документы компании"
                        name="curatorDocuments"
                        valuePropName="fileList"
                        getValueFromEvent={mapUploadValue}
                    >
                        <Upload {...buildUploadProps()}>
                            <Button icon={<UploadOutlined />}>Выбрать и загрузить</Button>
                        </Upload>
                    </Form.Item>

                </Form>
            </Modal>

            <Modal
                open={docsModalOpen}
                title={docsModalTitle}
                footer={null}
                onCancel={() => setDocsModalOpen(false)}
                destroyOnClose
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    {docsModalItems.map((file) => (
                        <div key={file.id} className="flex items-center justify-between rounded border border-slate-200 p-2">
                            <Typography.Text>{file.name}</Typography.Text>
                            <Button type="link" href={file.url || "#"} target="_blank" download={file.name}>
                                Скачать
                            </Button>
                        </div>
                    ))}
                </Space>
            </Modal>
        </div>
    );
};

export default ApplicationsManagementPage;
